/* src/components/customGame/player/CustomGamePlayerScreen.js */
import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } // Removed useBlocker as it's not implemented in this iteration
from 'react-router-dom';

// --- Custom Game Context ---
import { useCustomGame } from '../../../contexts/customGame/CustomGameContext';

// --- Custom Game Player UI Components ---
import CustomGameCountdown from './CustomGameCountdown';
import CustomGameHeader from './CustomGameHeader';
import CustomMatchButton from './CustomMatchButton';
import CustomGameResults from './CustomGameResults';
import CustomGamePaused from './CustomGamePaused';
// Placeholder for dynamic stimulus display area
// import DynamicStimulusPresenter from './DynamicStimulusPresenter';

// --- Shared UI Components ---
import LoadingScreen from '../../ui/LoadingScreen';
// import ConfirmationModal from '../../ui/ConfirmationModal'; // Keep if back navigation blocking is re-added
import FeedbackButton from '../../feedback/FeedbackButton';


const CustomGamePlayerScreen = () => {
    const navigate = useNavigate();
    const {
        config,
        gameState,
        currentTrial,
        totalTrials,
        sequence,
        score, // Added score from context
        gameResults,
        error,
        isLoadingSequence,

        prepareCustomGameSequence,
        // startCountdown, // No longer called directly from here; context handles it
        startGamePlay,
        pauseCustomGame,
        resumeCustomGame,
        endCustomGame,
        resetForNewSession,
        // registerCustomResponse, // Placeholder for future
    } = useCustomGame();

    // Local state (e.g., for confirmation modals if re-added)
    // const [showLeaveConfirmModal, setShowLeaveConfirmModal] = useState(false);

    // --- Effect to initiate sequence generation ---
    useEffect(() => {
        if (gameState === 'idle' && config && config.settings && !isLoadingSequence) {
            console.log('[CustomGamePlayerScreen] Effect: gameState is idle, preparing sequence with config:', config.presetMetadata.name);
            prepareCustomGameSequence();
        } else if (!config || !config.settings) {
            console.error('[CustomGamePlayerScreen] Effect: Attempted to start with no valid config. Navigating to setup.');
            navigate('/custom-game/setup', { replace: true });
        }
    }, [gameState, prepareCustomGameSequence, config, isLoadingSequence, navigate]);


    // --- Callbacks for UI interactions ---
    const handlePauseResume = useCallback(() => {
        if (gameState === 'playing') {
            pauseCustomGame();
        } else if (gameState === 'paused') {
            resumeCustomGame();
        }
    }, [gameState, pauseCustomGame, resumeCustomGame]);

    const handleMatchResponse = useCallback((stimulusKey) => {
        if (gameState !== 'playing') return;
        console.log(`[CustomGamePlayerScreen] Match response for: ${stimulusKey}`);
        // TODO: Call context action: registerCustomResponse(stimulusKey);
        // This action in context will:
        // 1. Record the response with timestamp.
        // 2. Check against sequence[currentTrial].matches[stimulusKey].
        // 3. Update score, feedback states (for buttons/global feedback).
        // 4. Potentially log for replay.
        alert(`Match button clicked for ${stimulusKey}. Response logic TBD.`);
    }, [gameState /*, currentTrial, sequence, registerCustomResponse */]);


    // --- Callbacks for Child Components ---
    const handleCountdownComplete = useCallback(() => {
        console.log('[CustomGamePlayerScreen] Countdown visual complete signal received.');
        startGamePlay(); // Action from context to transition gameState to 'playing'
    }, [startGamePlay]);

    const handleCancelCountdown = useCallback(() => {
        console.log('[CustomGamePlayerScreen] Countdown cancelled by user.');
        if (typeof resetForNewSession === 'function') {
            resetForNewSession(); // Clear game state in context
        }
        navigate('/custom-game/setup', { replace: true });
    }, [navigate, resetForNewSession]);

    const handleRetryCustomGame = useCallback(() => {
        console.log('[CustomGamePlayerScreen] Retry custom game requested.');
        if (typeof resetForNewSession === 'function') {
            resetForNewSession();
            // The useEffect for gameState 'idle' will then call prepareCustomGameSequence again
        }
    }, [resetForNewSession]);

    const handleGoToDashboard = useCallback(() => {
        if (typeof resetForNewSession === 'function') resetForNewSession();
        navigate('/dashboard');
    }, [navigate, resetForNewSession]);

    const handleConfigureNewCustomGame = useCallback(() => {
        if (typeof resetForNewSession === 'function') resetForNewSession();
        navigate('/custom-game/setup');
    }, [navigate, resetForNewSession]);

    const handleEndGameFromPause = useCallback(() => {
        console.log("[CustomGamePlayerScreen] User chose to End Game from pause menu.");
        // The endCustomGame action in context should handle calculating partial results if applicable
        // or simply marking the game as ended by user.
        endCustomGame({ status: 'ended_by_user_from_pause', finalTrial: currentTrial, score });
    }, [endCustomGame, currentTrial, score]);


    // --- Render Logic based on gameState ---

    if (!config || !config.settings) {
        // This should be caught by useEffect redirecting, but as a defensive measure:
        useEffect(() => { if (!config || !config.settings) navigate('/custom-game/setup', { replace: true }); }, [config, navigate]);
        return <LoadingScreen message="Configuration error. Redirecting..." />;
    }
    
    if (isLoadingSequence || (gameState === 'idle' && !error)) {
        return <LoadingScreen message="Preparing your custom game..." />;
    }

    if (error && gameState !== 'results') { // Don't show full error screen if results are being displayed with a message
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-red-50 text-red-700 p-8">
                <h2 className="text-2xl font-semibold mb-4">Error Preparing Game</h2>
                <p className="mb-4">{typeof error === 'object' ? JSON.stringify(error) : error}</p>
                <button
                    onClick={() => {
                        if (typeof resetForNewSession === 'function') resetForNewSession(); // Clear error in context too
                        navigate('/custom-game/setup', { replace: true });
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                    Back to Setup
                </button>
            </div>
        );
    }

    if (gameState === 'countdown') {
        const nValueDisplay = config.settings.Session?.NValue?.Mode === 'Fixed'
            ? String(config.settings.Session.NValue.FixedValue) // Ensure string for prop type
            : (config.settings.Session?.NValue?.Mode || 'N/A');
        return (
            <CustomGameCountdown
                onComplete={handleCountdownComplete}
                onCancel={handleCancelCountdown}
                nBack={nValueDisplay}
                isChallenge={false} // Custom games are not standard challenges
            />
        );
    }

    if (gameState === 'playing' || gameState === 'paused') {
        if (!sequence || sequence.length === 0 || currentTrial === undefined || currentTrial < 0) {
            // This state can occur briefly if sequence is being set after countdown started by mistake
            // Or if currentTrial hasn't been initialized yet by START_PLAYING
            console.log('[CustomGamePlayerScreen] Waiting for sequence/currentTrial initialization for playing state.');
            return <LoadingScreen message="Initializing gameplay..." />;
        }
        // Ensure currentStimuli is an object even if sequence[currentTrial] or sequence[currentTrial].stimuli is undefined
        const currentStimuliData = sequence[currentTrial]?.stimuli || {};
        const enabledTypes = config.settings.Stimulus?.EnabledTypes || [];
        const customKeyMappings = config.settings.Controls?.KeyMapping?.Custom || {};


        return (
            <div className="flex flex-col h-screen bg-gray-700 text-white items-center p-0 relative overflow-hidden">
                <CustomGameHeader
                    currentTrial={currentTrial}
                    totalTrials={totalTrials}
                    score={score}
                    onPauseResume={handlePauseResume}
                    isPaused={gameState === 'paused'}
                    customGameName={config.presetMetadata?.name}
                />

                <div className="flex-1 flex flex-col items-center justify-center w-full max-w-3xl my-4 p-4 overflow-y-auto">
                    {/* <p className="text-xl mb-2">Trial: {currentTrial + 1} / {totalTrials}</p> */} {/* Moved to header */}
                    {/* TODO: Dynamic Stimulus Rendering Area */}
                    <div className="bg-gray-600 p-8 rounded-lg shadow-xl min-h-[200px] w-full flex items-center justify-center text-center">
                        <div className="space-y-2">
                            <p className="text-gray-400">
                                Dynamic Stimulus Display Area (Trial {currentTrial + 1})
                            </p>
                            {enabledTypes.map(type => (
                                <div key={type} className="mx-1 p-1 bg-gray-500 rounded text-xs">
                                    <strong>{type}: </strong>
                                    <span>{JSON.stringify(currentStimuliData[type.toLowerCase()]?.value) || "N/A"}</span>
                                    {sequence[currentTrial]?.matches?.[type.toLowerCase()] && <span className="ml-2 text-green-300 font-bold">(MATCH!)</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {((config.settings.Controls?.InputMethods || []).includes('Mouse Click') ||
                  (config.settings.Controls?.InputMethods || []).includes('Touch')) &&
                 config.settings.Controls?.ShowOnScreenButtons && (
                    <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 p-4 w-full max-w-md mx-auto">
                        {enabledTypes.map(typeKey => {
                            let buttonLabel = typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
                            if (typeKey.toLowerCase() === 'position') buttonLabel = 'Position';

                            const actionKeyForMapping = `${typeKey}Match`;
                            const assignedHotkey = customKeyMappings[actionKeyForMapping];

                            return (
                                <CustomMatchButton
                                    key={typeKey}
                                    stimulusKey={typeKey.toLowerCase()}
                                    label={`${buttonLabel}`} // Removed "Match" to make buttons smaller
                                    onClick={handleMatchResponse}
                                    hotkey={assignedHotkey}
                                    disabled={gameState !== 'playing'}
                                />
                            );
                        })}
                    </div>
                )}


                {gameState === 'paused' && (
                    <CustomGamePaused
                        onResume={resumeCustomGame}
                        onEndGame={handleEndGameFromPause}
                    />
                )}
                <FeedbackButton />
            </div>
        );
    }

    if (gameState === 'results') {
        if (!gameResults) {
            // This could happen if endCustomGame was called without proper results
             console.warn('[CustomGamePlayerScreen] In results state but gameResults is null. Using placeholder.');
            return (
                <CustomGameResults
                    gameResults={{ message: "Session ended without detailed results." }}
                    config={config}
                    onRetry={handleRetryCustomGame}
                    onConfigureNew={handleConfigureNewCustomGame}
                    onGoToDashboard={handleGoToDashboard}
                />
            );
        }
        return (
            <CustomGameResults
                gameResults={gameResults}
                config={config}
                onRetry={handleRetryCustomGame}
                onConfigureNew={handleConfigureNewCustomGame}
                onGoToDashboard={handleGoToDashboard}
            />
        );
    }
    
    // Fallback / Should not be easily reached
    console.warn(`[CustomGamePlayerScreen] Reached fallback render with gameState: ${gameState}`);
    return <LoadingScreen message="Loading Custom Game..." />;
};

export default CustomGamePlayerScreen;